import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import {
  DynamicQuestionnaireFlow,
  getOrderedQuestions,
  injectedAnswerKey,
} from '@/components/questionnaires/json-driven/DynamicQuestionnaireFlow';
import {
  isAnsweredValue,
  normalizeQuestionAnswerForSubmit,
  OTHER_TEXT_SUFFIX,
} from '@/components/questionnaires/json-driven/QuestionRenderer';
import { getVisibleOrderedQuestions } from '@/components/questionnaires/json-driven/visibleQuestions';
import {
  buildQuestionnairePrefillScopeKey,
  buildQuestionnairePrefillFromAssignedClient,
  resolveQuestionnaireDemographicContext,
  triggerContextFromAssignedClient,
} from '@/components/questionnaires/json-driven/clientContext';
import { getQuestionKey } from '@/components/questionnaires/json-driven/questionKey';
import { isEffectivelyRequired } from '@/components/questionnaires/json-driven/effectiveRequired';
import type { JsonAnswerValue } from '@/components/questionnaires/json-driven/QuestionRenderer';
import type { JsonQuestion, JsonQuestionOption, JsonQuestionnaireSection } from '@/components/questionnaires/json-driven/types';
import type { TriggerConditionContext, TriggeredBlock } from '@/lib/questionnaire-triggers';
import { useQuery } from '@tanstack/react-query';
import {
  getQuestionnaireByIdForTriggers,
  getSharedQuestionnaire,
  submitQuestionnaire,
  INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE,
  type SubmitQuestionnaireBlock,
  type SubmitQuestionnairePayload,
} from '@/lib/api/questionnaires';
import {
  applyPendingSavedResponses,
  buildQuestionnaireBlocks,
  getTriggeredSectionAnswers,
} from '@/lib/questionnaire-draft';
import { cn } from '@/lib/utils';

type AnswerMap = Record<string, JsonAnswerValue>;

function isAnswered(question: JsonQuestion, value: JsonAnswerValue): boolean {
  if (question.type === 'number-unit') {
    return normalizeQuestionAnswerForSubmit(question, value) !== undefined;
  }

  return isAnsweredValue(value);
}

function isDoNotConsentOption(option: JsonQuestionOption): boolean {
  const normalized = `${option.label} ${option.value}`
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.includes('do not consent') || normalized.includes('do not share');
}

function questionHasDoNotConsentOption(question: JsonQuestion): boolean {
  return question.options?.some(isDoNotConsentOption) ?? false;
}

function isDoNotConsentAnswer(question: JsonQuestion, value: JsonAnswerValue): boolean {
  const doNotConsentValues = new Set(
    (question.options ?? []).filter(isDoNotConsentOption).map((option) => option.value)
  );
  if (doNotConsentValues.size === 0) return false;
  if (typeof value === 'string') return doNotConsentValues.has(value);
  if (Array.isArray(value)) return value.some((selectedValue) => doNotConsentValues.has(selectedValue));
  return value === true && (question.options ?? []).length === 1;
}

function hasDoNotConsentSelection(
  section: JsonQuestionnaireSection,
  answers: AnswerMap,
  storageSectionId?: string
): boolean {
  for (const question of getOrderedQuestions(section)) {
    const questionId = getQuestionKey(question);
    const answerKey = storageSectionId ? injectedAnswerKey(storageSectionId, questionId) : questionId;
    if (isDoNotConsentAnswer(question, answers[answerKey])) return true;
  }
  return false;
}

const PublicQuestionnairePage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();

  const {
    data: sharedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['sharedQuestionnaire', shareId],
    enabled: !!shareId,
    queryFn: () => getSharedQuestionnaire(shareId as string),
  });

  const section = sharedData?.section;
  const assignedClient = sharedData?.assignedClient;
  const triggerContext = useMemo<TriggerConditionContext | undefined>(
    () => triggerContextFromAssignedClient(assignedClient),
    [assignedClient]
  );
  const assignedPrefill = useMemo(
    () => (section && assignedClient ? buildQuestionnairePrefillFromAssignedClient(section, assignedClient) : {}),
    [section, assignedClient]
  );
  const assignedPrefillScopeKey = useMemo(
    () => buildQuestionnairePrefillScopeKey(section?.id, assignedPrefill),
    [assignedPrefill, section?.id]
  );

  const recallDates = sharedData?.recallDates;
  const dietRecallProgress = sharedData?.dietRecallProgress;
  const numberOfDays = sharedData?.numberOfDays ?? recallDates?.length ?? 0;
  const isMultiDayRecall = numberOfDays > 1 && (recallDates?.length ?? 0) > 0;

  const [selectedRecallIndex, setSelectedRecallIndex] = useState(0);
  const selectedRecallDate = useMemo(
    () => (recallDates && recallDates[selectedRecallIndex] ? recallDates[selectedRecallIndex] : undefined),
    [recallDates, selectedRecallIndex]
  );

  /** Answers per day index for multi-day recall; for single-day, use key 0. */
  const [answersByDay, setAnswersByDay] = useState<Record<number, AnswerMap>>({ 0: {} });
  /** Current day's answers (derived). */
  const answers = answersByDay[selectedRecallIndex] ?? {};
  const [errorsByDay, setErrorsByDay] = useState<Record<number, Record<string, string>>>({ 0: {} });
  const errors = errorsByDay[selectedRecallIndex] ?? {};
  const [triggeredBlocksByDay, setTriggeredBlocksByDay] = useState<Record<number, TriggeredBlock[]>>({ 0: [] });
  const triggeredBlocks = triggeredBlocksByDay[selectedRecallIndex] ?? [];
  const [showConsentDeclinedDialog, setShowConsentDeclinedDialog] = useState(false);
  const appliedAssignedPrefillKeysRef = useRef<Set<string>>(new Set());
  const appliedAssignedPrefillScopeRef = useRef('');
  const appliedSavedResponseFingerprintsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    setAnswersByDay({ 0: {} });
    setErrorsByDay({ 0: {} });
    setTriggeredBlocksByDay({ 0: [] });
    setSubmitted(false);
    setSubmittedDays(new Set());
    appliedAssignedPrefillKeysRef.current = new Set();
    appliedAssignedPrefillScopeRef.current = '';
    appliedSavedResponseFingerprintsRef.current = new Map();
  }, [shareId]);

  const hasConsentDeclined = useMemo(() => {
    if (!section) return false;
    if (hasDoNotConsentSelection(section, answers)) return true;
    return triggeredBlocks.some((block) =>
      hasDoNotConsentSelection(block.section, answers, block.meta.questionnaireId)
    );
  }, [answers, section, triggeredBlocks]);

  const shouldDisableQuestionForConsentDecline = useCallback(
    (question: JsonQuestion) => hasConsentDeclined && !questionHasDoNotConsentOption(question),
    [hasConsentDeclined]
  );

  const demographicContext = useMemo(
    () => resolveQuestionnaireDemographicContext(answers, assignedClient, section ?? null),
    [answers, assignedClient, section]
  );

  useEffect(() => {
    if (!section) return;
    if (appliedAssignedPrefillScopeRef.current !== assignedPrefillScopeKey) {
      appliedAssignedPrefillKeysRef.current.clear();
      appliedAssignedPrefillScopeRef.current = assignedPrefillScopeKey;
    }
    const appliedPrefillKeys = appliedAssignedPrefillKeysRef.current;
    const pendingPrefillEntries = Object.entries(assignedPrefill).filter(([key]) => {
      if (appliedPrefillKeys.has(key)) return false;
      appliedPrefillKeys.add(key);
      return true;
    });

    setAnswersByDay((prev) => {
      const dayAnswers = prev[0] ?? {};
      let changed = false;
      const nextDay: AnswerMap = { ...dayAnswers };

      for (const [key, value] of pendingPrefillEntries) {
        const current = nextDay[key];
        if (current === undefined || current === '' || (Array.isArray(current) && current.length === 0)) {
          nextDay[key] = value;
          changed = true;
        }
      }

      const draftPatch = applyPendingSavedResponses(
        section,
        triggeredBlocks,
        sharedData?.savedResponses,
        appliedSavedResponseFingerprintsRef.current
      );
      if (Object.keys(draftPatch).length > 0) {
        Object.assign(nextDay, draftPatch);
        changed = true;
      }

      if (!changed) return prev;
      return { ...prev, 0: nextDay };
    });
  }, [section, assignedPrefill, assignedPrefillScopeKey, sharedData?.savedResponses, triggeredBlocks]);

  const validate = useCallback(() => {
    if (!section) return false;
    const nextErrors: Record<string, string> = {};
    const visibleBase = getVisibleOrderedQuestions(section, answers, demographicContext);
    for (const q of visibleBase) {
      if (!isEffectivelyRequired(q, (id) => answers[id])) continue;
      const key = getQuestionKey(q);
      if (!isAnswered(q, answers[key])) nextErrors[key] = 'This question is required.';
    }

    for (const block of triggeredBlocks) {
      const { section: sec, meta } = block;
      const storageId = meta.questionnaireId;
      const triggeredAnswers = getTriggeredSectionAnswers(sec, answers, storageId);
      const visibleTriggered = getVisibleOrderedQuestions(sec, triggeredAnswers, demographicContext);
      for (const q of visibleTriggered) {
        if (!isEffectivelyRequired(q, (id) => triggeredAnswers[id])) continue;
        const key = injectedAnswerKey(storageId, getQuestionKey(q));
        if (!isAnswered(q, answers[key])) nextErrors[key] = 'This question is required.';
      }
    }

    setErrorsByDay((prev) => ({ ...prev, [selectedRecallIndex]: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  }, [section, answers, triggeredBlocks, selectedRecallIndex, demographicContext]);

  const buildSubmitQuestionnaires = useCallback((): SubmitQuestionnaireBlock[] | null => {
    if (!section) return null;
    const blocks = buildQuestionnaireBlocks(section, answers, triggeredBlocks, demographicContext);
    return blocks.length > 0 ? blocks : null;
  }, [section, answers, triggeredBlocks, demographicContext]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  /** For single-day: global submitted. For multi-day: unused, we use submittedDays. */
  const [submitted, setSubmitted] = useState(false);
  /** Days (1-based) submitted in this session; merged with server progress for instant UI update. */
  const [submittedDays, setSubmittedDays] = useState<Set<number>>(new Set());

  /** Effective completed days = server progress + just-submitted days (no refetch needed). */
  const effectiveCompletedDayNumbers = useMemo(
    () => new Set<number>([...(dietRecallProgress?.completedDayNumbers ?? []), ...submittedDays]),
    [dietRecallProgress?.completedDayNumbers, submittedDays]
  );
  const effectiveCompletedCount = effectiveCompletedDayNumbers.size;
  const totalCount = dietRecallProgress?.total ?? numberOfDays ?? 1;
  const isCurrentDaySubmitted = isMultiDayRecall
    ? effectiveCompletedDayNumbers.has(selectedRecallIndex + 1)
    : submitted;

  const onSubmit = async () => {
    if (hasConsentDeclined) {
      setShowConsentDeclinedDialog(true);
      return;
    }
    if (!validate() || !shareId) return;
    const questionnaires = buildSubmitQuestionnaires();
    if (!questionnaires?.length) {
      showErrorToast(new Error(INVALID_SUBMIT_QUESTIONNAIRE_PAYLOAD_MESSAGE), 'Failed to submit questionnaire');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: SubmitQuestionnairePayload = { questionnaires };
      if (isMultiDayRecall && selectedRecallDate) {
        payload.dayNumber = selectedRecallIndex + 1;
        payload.recallDate = selectedRecallDate;
      }
      await submitQuestionnaire(shareId, payload);
      if (isMultiDayRecall) {
        setSubmittedDays((prev) => new Set(prev).add(selectedRecallIndex + 1));
      } else {
        setSubmitted(true);
      }
      const completedCount = isMultiDayRecall ? effectiveCompletedCount + 1 : 1;
      if (isMultiDayRecall && totalCount > 1) {
        showSuccessToast(
          'Submitted',
          completedCount >= totalCount
            ? `You've completed all ${totalCount} days. Thank you.`
            : `Day ${selectedRecallIndex + 1} submitted. You have ${totalCount - completedCount} more day(s) to complete.`
        );
      } else {
        showSuccessToast('Submitted', 'Your responses have been submitted.');
      }
    } catch (err) {
      showErrorToast(err as unknown, 'Failed to submit questionnaire');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOptionSelect = useCallback((_question: JsonQuestion, option: JsonQuestionOption) => {
    if (isDoNotConsentOption(option)) {
      setShowConsentDeclinedDialog(true);
    }
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Loading questionnaire…</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">Please wait.</CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Could not load questionnaire</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {(error as Error | undefined)?.message ?? 'An unexpected error occurred.'}
            </p>
            <Button
              variant="outline"
              onClick={() => showErrorToast(error as unknown, 'Failed to load questionnaire')}
            >
              Report issue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!shareId || !section) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <CardTitle>Questionnaire not found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              We couldn’t find this questionnaire section.
            </p>
            <Button
              variant="outline"
              onClick={() => showErrorToast(new Error('Section not found'), 'Invalid questionnaire link')}
            >
              Report issue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-8 sm:py-10 space-y-8">
        {isMultiDayRecall && recallDates && recallDates.length > 0 && (
          <Card className="font-manrope shadow border-border/80 rounded-xl overflow-hidden">
            <CardContent className="p-4 sm:p-5">
              <p className="text-sm font-medium text-foreground mb-2">
                Which day are you filling in?
              </p>
              {dietRecallProgress && (
                <p className="text-xs text-muted-foreground mb-3">
                  Completed {effectiveCompletedCount} of {totalCount} day
                  {totalCount !== 1 ? 's' : ''}.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {recallDates.map((dateStr, index) => {
                  const isCompleted = effectiveCompletedDayNumbers.has(index + 1);
                  const isSelected = selectedRecallIndex === index;
                  let label: string;
                  try {
                    const d = parseISO(dateStr);
                    label = format(d, 'EEE, MMM d, yyyy');
                  } catch {
                    label = dateStr;
                  }
                  return (
                    <Button
                      key={dateStr}
                      type="button"
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      className={cn(
                        'rounded-lg',
                        isCompleted && !isSelected && 'border-emerald-500/50 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'
                      )}
                      onClick={() => setSelectedRecallIndex(index)}
                    >
                      Day {index + 1}: {label}
                      {isCompleted ? ' ✓' : ''}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="font-manrope shadow-md border-border/80 rounded-xl overflow-hidden">
          <CardContent className="p-6 sm:p-8">
            {isCurrentDaySubmitted ? (
              <div className="py-6 text-center">
                <p className="text-base font-medium text-emerald-600 mb-2">
                  {isMultiDayRecall ? 'This day has been submitted.' : 'Thank you for completing this questionnaire.'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isMultiDayRecall
                    ? 'Switch to another day above to fill in more days, or you\'re done if all days are complete.'
                    : 'Your responses have been received.'}
                </p>
              </div>
            ) : (
              <>
                {isMultiDayRecall && selectedRecallDate && (
                  <p className="text-sm text-muted-foreground mb-4 pb-4 border-b">
                    Recalling intake for{' '}
                    {(() => {
                      try {
                        return format(parseISO(selectedRecallDate), 'EEEE, MMMM d, yyyy');
                      } catch {
                        return selectedRecallDate;
                      }
                    })()}
                  </p>
                )}
                <DynamicQuestionnaireFlow
                  section={section}
                  answers={answers}
                  triggerContext={triggerContext}
                  assignedClient={assignedClient}
                  hideTitle
                  hideDescription
                  hideQuestionNotes
                  questionVariant="popup"
                  onAnswerChange={(questionKey, value) => {
                    setAnswersByDay((prev) => ({
                      ...prev,
                      [selectedRecallIndex]: { ...(prev[selectedRecallIndex] ?? {}), [questionKey]: value },
                    }));
                    setErrorsByDay((prev) => {
                      if (!prev[selectedRecallIndex]?.[questionKey]) return prev;
                      const copy = { ...prev, [selectedRecallIndex]: { ...(prev[selectedRecallIndex] ?? {}) } };
                      delete copy[selectedRecallIndex][questionKey];
                      return copy;
                    });
                  }}
                  errors={errors}
                  fetchQuestionnaire={getQuestionnaireByIdForTriggers}
                  onTriggeredChange={(blocks) => {
                    setTriggeredBlocksByDay((prev) => ({ ...prev, [selectedRecallIndex]: blocks }));
                  }}
                  onOptionSelect={handleOptionSelect}
                  isQuestionDisabled={shouldDisableQuestionForConsentDecline}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Sticky submit bar */}
        <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-4 pt-2 bg-muted/30">
          <div className="bg-card/95 backdrop-blur-sm border border-border/80 rounded-xl shadow-lg py-4 px-5 sm:px-6">
            {!isCurrentDaySubmitted && Object.keys(errors).length > 0 && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive font-medium mb-4" role="alert">
                Please answer all required questions before submitting.
              </div>
            )}
            {!isCurrentDaySubmitted && Object.keys(errors).length === 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Review your answers, then submit.
              </p>
            )}
            <div className="flex items-center justify-end">
              <Button
                onClick={onSubmit}
                size="lg"
                className="rounded-lg min-w-[120px]"
                disabled={isSubmitting || isCurrentDaySubmitted || hasConsentDeclined}
              >
                {isSubmitting ? 'Submitting…' : isCurrentDaySubmitted ? 'Submitted' : 'Submit'}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <AlertDialog open={showConsentDeclinedDialog} onOpenChange={setShowConsentDeclinedDialog}>
        <AlertDialogContent className="max-w-[420px] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Information sharing declined</AlertDialogTitle>
            <AlertDialogDescription className="text-[15px] leading-6 text-slate-600">
              Please inform your practitioner that you have chosen not to share this information. This may
              result in them having limited information to support your personalized care.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction className="rounded-lg bg-[#20AEDC] hover:bg-[#1a99c0]">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PublicQuestionnairePage;
